/**
 * Abstract Syntax Tree (AST) type definitions for Feynman DSL.
 *
 * These types represent the parsed structure of an animation sequence
 * before it's rendered into output formats.
 */

/**
 * Arrow types for interactions between participants.
 * Maps to different visual representations in the output.
 */
export type ArrowType = '->' | '-->' | '->>' | '-->>';

/**
 * Animation directive types that control participant visibility and emphasis.
 */
export type DirectiveType =
  | 'focus'
  | 'dim'
  | 'show'
  | 'hide'
  | 'activate'
  | 'deactivate';

/**
 * A participant (actor) in the sequence diagram.
 */
export interface Participant {
  name: string;
  /**
   * Optional custom color for this participant (hex color code)
   * Example: participant User (color=#ff6b6b)
   */
  color?: string;
}

/**
 * An animation directive that controls how participants are displayed.
 *
 * @example
 * { type: 'focus', participants: ['Client', 'API'] }
 */
export interface Directive {
  type: DirectiveType;
  participants: string[];
}

/**
 * An interaction (message) between two participants.
 *
 * @example
 * {
 *   from: 'Client',
 *   to: 'API',
 *   message: 'POST /users',
 *   arrow: '->'
 * }
 */
export interface Interaction {
  from: string;
  to: string;
  message: string;
  arrow: ArrowType;
}

/**
 * Position for a note annotation.
 */
export type NotePosition = 'over' | 'left' | 'right';

/**
 * A note annotation on the diagram.
 *
 * @example
 * {
 *   position: 'over',
 *   participants: ['Client'],
 *   text: 'User must be authenticated'
 * }
 */
export interface Note {
  position: NotePosition;
  participants: string[];
  text: string;
}

/**
 * Fragment types for control flow blocks.
 */
export type FragmentType = 'alt' | 'opt' | 'loop' | 'par';

/**
 * A fragment block containing interactions with a label.
 */
export interface Fragment {
  type: FragmentType;
  label: string;
  interactions: Interaction[];
}

/**
 * A step in the animation sequence.
 * Each step contains directives, interactions, notes, and fragments that appear during that step.
 */
export interface Step {
  description: string;
  directives: Directive[];
  interactions: Interaction[];
  notes: Note[];
  fragments: Fragment[];
  /**
   * Optional duration in milliseconds for this step
   * Example: step "Quick action" (duration=1s) -> 1000ms
   */
  duration?: number;
  /**
   * Optional custom color for this step (hex color code)
   * Example: step "Error case" (color=#f03e3e)
   */
  color?: string;
}

/**
 * The root AST node representing a complete animation sequence.
 */
export interface AnimationSequence {
  title: string;
  participants: Participant[];
  steps: Step[];
}

/**
 * Error class for parse errors with location information.
 */
export class ParseError extends Error {
  constructor(
    message: string,
    public line: number,
    public column: number
  ) {
    super(message);
    this.name = 'ParseError';
  }
}
